/**
 * Database Schema Model for FreshCards
 * Database structure with schema, views, and items
 */

import type { PropertyDefinition, PropertyValue, SelectOption, StatusOption, NumberFormat } from './Property';

export interface Database {
  id: string;
  name: string;
  icon?: string;
  cover?: CardCover;
  schema: DatabaseSchema;
  views: ViewConfig[];
  items: DatabaseItem[];
  createdAt: Date;
  updatedAt: Date;
}

export interface DatabaseSchema {
  id: string;
  properties: PropertyDefinition[];
}

export interface DatabaseItem {
  id: string;
  databaseId: string;
  title: string;
  icon?: string;
  cover?: CardCover;
  properties: PropertyValue[];
  tags: string[];   // flat tag list for display in peek Details tab
  createdAt: Date;
  updatedAt: Date;
}

export interface ViewConfig {
  id: string;
  name: string;
  type: ViewType;
  filters?: Filter[];
  sorts?: Sort[];
}

export type ViewType = 'gallery' | 'kanban' | 'list' | 'calendar';

export interface CardCover {
  type: 'external' | 'file';
  url: string;
}

export interface Filter {
  propertyId: string;
  operator: FilterOperator;
  value: unknown;
}

export type FilterOperator = 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than';

export interface Sort {
  propertyId: string;
  direction: 'asc' | 'desc';
}

/**
 * Creates a new database with default schema and view
 */
export function createDatabase(id: string, name: string = 'Untitled Database'): Database {
  const now = new Date();
  return {
    id,
    name,
    schema: {
      id: `${id}_schema`,
      properties: [],
    },
    views: [
      createDefaultView(`${id}_default_view`, 'list'),
    ],
    items: [],
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Creates a default view configuration
 */
export function createDefaultView(id: string, type: ViewType = 'list'): ViewConfig {
  return {
    id,
    name: type.charAt(0).toUpperCase() + type.slice(1),
    type,
  };
}

/**
 * Creates a gallery view
 */
export function createGalleryView(id: string, name: string = 'Gallery'): ViewConfig {
  return {
    id,
    name,
    type: 'gallery',
  };
}

/**
 * Creates a kanban view
 */
export function createKanbanView(id: string, name: string = 'Board', groupByPropertyId?: string): ViewConfig {
  const view = createDefaultView(id, 'kanban');
  if (groupByPropertyId) {
    view.filters = [{
      propertyId: groupByPropertyId,
      operator: 'equals',
      value: undefined,
    }];
  }
  return view;
}

/**
 * Creates a calendar view
 */
export function createCalendarView(id: string, name: string = 'Calendar', datePropertyId?: string): ViewConfig {
  const view = createDefaultView(id, 'calendar');
  if (datePropertyId) {
    view.filters = [{
      propertyId: datePropertyId,
      operator: 'equals',
      value: undefined,
    }];
  }
  return view;
}

/**
 * Creates a database item
 */
export function createDatabaseItem(id: string, databaseId: string, title: string = 'Untitled'): DatabaseItem {
  const now = new Date();
  return {
    id,
    databaseId,
    title,
    properties: [],
    tags: [],
    createdAt: now,
    updatedAt: now,
  };
}